import { useTranslation } from 'next-i18next';
import { isEmpty } from 'ramda';
import { MouseEventHandler, ReactNode } from 'react';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { PremiumTiers } from '@bridebook/models/source/models/Suppliers/Admin/admin.types';
import { A, Box, Button } from '@bridebook/ui';
import { IconContact, IconWeb } from '@bridebook/ui/src/icons';
import SignupLink from '@bridebook/web/app-shared/components/signup-link/signup-link';
import BorderedContainer from '@bridebook/web/app-shared/components/supplier/sections/bordered-container/bordered-container';
import { formatUrl } from '@bridebook/web/app-shared/lib/supplier/utils/formatUrl';
import getUtmParams from '@bridebook/web/app-shared/lib/utils/get-utm-params';
import componentStyles from './contact-details-bar.style';
import ShowPhone, { IShowPhoneProps } from './show-phone';

interface IProps {
  onEmailClick?: MouseEventHandler<HTMLDivElement>;
  onWebsiteClick?: MouseEventHandler<ReactNode>;
  phoneProps: IShowPhoneProps;
  supplier: ISupplier | undefined;
  isLoggedIn: boolean;
  supplierTier?: PremiumTiers | number;
}

const ContactDetailsBar = ({
  supplier,
  onWebsiteClick,
  onEmailClick,
  isLoggedIn,
  phoneProps,
  supplierTier,
}: IProps) => {
  const { t } = useTranslation('supplier');

  if (!supplier) return null;

  const website = supplier?.contacts?.website;
  const phone = supplier?.contacts?.phone;
  const supplierWebsiteUrl = formatUrl(website, getUtmParams('profile'));

  const isPaidSupplier = (supplierTier || PremiumTiers.Tier_0) > PremiumTiers.Tier_0;
  const buttonList = [
    {
      name: 'website',
      item: (
        <A
          rel={!isPaidSupplier ? 'noopener nofollow' : undefined}
          href={supplierWebsiteUrl || ''}
          target="_blank"
          data-link-type="website"
          onClick={onWebsiteClick}>
          <Button theme="ghost" color="space" icon={<IconWeb />} text={t('button.website')} />
        </A>
      ),
      hidden: isEmpty(website),
    },
    {
      name: 'email',
      item: (
        <SignupLink
          onClick={onEmailClick}
          isLoggedIn={isLoggedIn}
          clickedLocation="supplierProfile"
          clickedSection="contactDetails"
          cta="email">
          <Button
            theme="ghost"
            color="space"
            icon={<IconContact width={19} />}
            text={isLoggedIn ? t('button.message') : t('button.email')}
          />
        </SignupLink>
      ),
    },
    {
      name: 'phone',
      item: <ShowPhone {...phoneProps} />,
      hidden: !phone,
    },
  ];

  const styles = componentStyles();

  return (
    <BorderedContainer>
      <Box style={styles.wrapper}>
        {buttonList.map(
          (section) =>
            !section.hidden && (
              <Box style={styles.buttonWrap} key={`contact-details-bar-button-${section.name}`}>
                {section.item}
              </Box>
            ),
        )}
      </Box>
    </BorderedContainer>
  );
};

export default ContactDetailsBar;
